import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  InputAdornment,
  Toolbar,
  Divider,
  CircularProgress,
  Avatar,
  CardMedia,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Download,
  Refresh,
  Print,
  DirectionsCar,
  PhotoCamera,
  UploadFile,
  OpenInNew,
  ArrowUpward,
  ArrowDownward,
  FilterListOff,
  AutoAwesome,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { documentApi, vehicleApi, vehicleCatalogApi } from '../services/api';
import { Vehicle, VehicleCatalogMake, VehicleCatalogModel, VehicleClass, VehicleDocument, VehicleType, VehicleStatus } from '../types/vehicle';
import { CURRENCY_SYMBOL, formatCurrency, formatDateTime } from '../utils/formatters';
import { safeFilePart } from '../utils/downloadFile';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TableFilterCell } from '../components/TableFilterCell';
import { fuelTypeLabel, statusLabel, vehicleClassLabel, vehicleTypeLabel } from '../i18n/translate';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

interface VehicleFormData {
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  vin: string;
  type: VehicleType;
  vehicleClass: VehicleClass;
  status: VehicleStatus;
  dailyRate: number;
  fuelType: string;
  transmission: string;
  engineSize: string;
  seatingCapacity: number;
  color: string;
  mileage: number;
  purchasePrice: string;
  purchaseDate: string;
  purchaseMileageKm: string;
  description: string;
  imageUrl: string;
  currentLocation: string;
  pickupLocation: string;
  isFeatured: boolean;
  isAvailableForRental: boolean;
}

const createDefaultFormData = (t: (key: string, defaultValue?: string) => string): VehicleFormData => ({
  make: '',
  model: '',
  year: new Date().getFullYear(),
  licensePlate: '',
  vin: '',
  type: VehicleType.CAR,
  vehicleClass: VehicleClass.MID_SIZE,
  status: VehicleStatus.AVAILABLE,
  dailyRate: 0,
  fuelType: 'Petrol',
  transmission: t('fleet.defaultTransmission', 'Automatic'),
  engineSize: '',
  seatingCapacity: 5,
  color: '',
  mileage: 0,
  purchasePrice: '',
  purchaseDate: '',
  purchaseMileageKm: '',
  description: '',
  imageUrl: '',
  currentLocation: '',
  pickupLocation: '',
  isFeatured: true,
  isAvailableForRental: true,
});

export const FleetManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDocumentsDialog, setOpenDocumentsDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedMakeId, setSelectedMakeId] = useState<number | null>(null);
  const [makeSearchInput, setMakeSearchInput] = useState('');
  const [modelSearchInput, setModelSearchInput] = useState('');
  const [debouncedMakeSearch, setDebouncedMakeSearch] = useState('');
  const [debouncedModelSearch, setDebouncedModelSearch] = useState('');
  
  // Column-level filters
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({
    vehicle: '',
    year: '',
    licensePlate: '',
    type: '',
    vehicleClass: '',
    status: '',
    dailyRate: '',
    mileage: '',
    location: '',
  });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Pending image file when creating new vehicle (upload after create)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const documentAccept = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt';
  const pendingImageFileRef = useRef<File | null>(null);
  const pendingImagePreviewRef = useRef<string | null>(null);
  const uploadChainedFromCreateRef = useRef(false);
  useEffect(() => {
    pendingImageFileRef.current = pendingImageFile;
  }, [pendingImageFile]);

  // AI extraction of vehicle registration documents (Leja e Qarkullimit)
  const [registrationFile, setRegistrationFile] = useState<File | null>(null);
  const registrationFileRef = useRef<File | null>(null);
  useEffect(() => {
    registrationFileRef.current = registrationFile;
  }, [registrationFile]);
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());
  const aiFilledFieldSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' } };

  const clearAiFilledField = (field: string) => {
    setAiFilledFields((current) => {
      if (!current.has(field)) return current;
      const next = new Set(current);
      next.delete(field);
      return next;
    });
  };

  // Bulk import: upload many registration documents, extract each with AI, and create draft vehicles for review
  interface ImportItem {
    file: File;
    status: 'queued' | 'processing' | 'done' | 'failed';
    error?: string;
    vehicleId?: number;
  }
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [importItems, setImportItems] = useState<ImportItem[]>([]);
  const [importRunning, setImportRunning] = useState(false);
  const [reviewQueue, setReviewQueue] = useState<number[]>([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const isReviewing = reviewQueue.length > 0;

  const columnDefs: Array<{ key: keyof typeof columnFilters; label: string; width: number }> = [
    { key: 'vehicle', label: t('fields.vehicle'), width: 200 },
    { key: 'year', label: t('fields.year'), width: 100 },
    { key: 'licensePlate', label: t('fields.licensePlate'), width: 130 },
    { key: 'type', label: t('fields.type'), width: 120 },
    { key: 'vehicleClass', label: t('fields.vehicleClass'), width: 120 },
    { key: 'status', label: t('fields.status'), width: 120 },
    { key: 'dailyRate', label: t('fields.dailyRate'), width: 130 },
    { key: 'mileage', label: t('fields.mileageKm'), width: 120 },
    { key: 'location', label: t('fields.location'), width: 150 },
  ];

  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<VehicleFormData>(() => createDefaultFormData(t));
  const toNullableNumber = (value: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toNullableInteger = (value: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const buildVehiclePayload = (
    overrides: Partial<Vehicle> & { imageUrl?: string | null } = {}
  ): Partial<Vehicle> & { imageUrl?: string | null } => ({
    make: formData.make.trim(),
    model: formData.model.trim(),
    year: formData.year,
    licensePlate: formData.licensePlate.trim(),
    vin: formData.vin.trim() || null,
    type: formData.type,
    vehicleClass: formData.vehicleClass,
    status: formData.status,
    dailyRate: formData.dailyRate,
    fuelType: formData.fuelType.trim(),
    transmission: formData.transmission.trim(),
    engineSize: formData.engineSize.trim(),
    seatingCapacity: formData.seatingCapacity,
    color: formData.color.trim(),
    mileage: formData.mileage,
    purchasePrice: toNullableNumber(formData.purchasePrice),
    purchaseDate: formData.purchaseDate || null,
    purchaseMileageKm: toNullableInteger(formData.purchaseMileageKm),
    description: formData.description.trim(),
    imageUrl: formData.imageUrl || '',
    currentLocation: formData.currentLocation.trim(),
    pickupLocation: formData.pickupLocation.trim(),
    isFeatured: formData.isFeatured,
    isAvailableForRental: formData.isAvailableForRental,
    ...overrides,
  });

  const loadVehicleIntoEditor = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setSelectedMakeId(null);
    setMakeSearchInput(vehicle.make || '');
    setModelSearchInput(vehicle.model || '');
    setFormData({
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      licensePlate: vehicle.licensePlate || '',
      vin: vehicle.vin || '',
      type: vehicle.type,
      vehicleClass: vehicle.vehicleClass || VehicleClass.MID_SIZE,
      status: vehicle.status,
      dailyRate: typeof vehicle.dailyRate === 'string' ? parseFloat(vehicle.dailyRate) : vehicle.dailyRate || 0,
      fuelType: vehicle.fuelType || 'Petrol',
      transmission: vehicle.transmission || t('fleet.defaultTransmission', 'Automatic'),
      engineSize: vehicle.engineSize || '',
      seatingCapacity: vehicle.seatingCapacity || 5,
      color: vehicle.color || '',
      mileage: vehicle.mileage || 0,
      purchasePrice: vehicle.purchasePrice != null ? String(vehicle.purchasePrice) : '',
      purchaseDate: vehicle.purchaseDate || '',
      purchaseMileageKm: vehicle.purchaseMileageKm != null ? String(vehicle.purchaseMileageKm) : '',
      description: vehicle.description || '',
      imageUrl: vehicle.imageUrl || vehicle.imageUrls?.[0] || '',
      currentLocation: vehicle.currentLocation || '',
      pickupLocation: vehicle.pickupLocation || '',
      isFeatured: vehicle.isFeatured ?? true,
      isAvailableForRental: vehicle.isAvailableForRental !== false,
    });
    setIsCreating(false);
    setEditMode(true);
    setIsDirty(false);
    setRegistrationFile(null);
    setAiFilledFields(new Set());
  };

  // Fetch vehicles
  const { data: vehicles = [], isLoading } = useQuery('vehicles', vehicleApi.getAll);
  const pendingReviewVehicles = vehicles.filter((vehicle) => vehicle.pendingReview);
  const { data: makeOptions = [], isLoading: isLoadingMakeOptions } = useQuery(
    ['vehicleCatalogMakes', debouncedMakeSearch],
    () => vehicleCatalogApi.getMakes(debouncedMakeSearch, 50),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: modelOptions = [], isLoading: isLoadingModelOptions } = useQuery(
    ['vehicleCatalogModels', selectedMakeId, formData.make, debouncedModelSearch],
    () =>
      vehicleCatalogApi.getModels({
        makeId: selectedMakeId ?? undefined,
        makeName: selectedMakeId ? undefined : formData.make.trim(),
        query: debouncedModelSearch,
        limit: 50,
      }),
    {
      enabled: !!formData.make.trim(),
      staleTime: 5 * 60 * 1000,
    }
  );
  const {
    data: vehicleDocuments = [],
    isLoading: isLoadingVehicleDocuments,
  } = useQuery(
    ['vehicleDocuments', selectedVehicle?.id],
    () => vehicleApi.getDocuments(selectedVehicle!.id.toString()),
    {
      enabled: openDocumentsDialog && !!selectedVehicle,
    }
  );

  // Create mutation
  const createMutation = useMutation(vehicleApi.create, {
    onSuccess: (createdVehicle) => {
      const docFile = registrationFileRef.current;

      const finalizeCreateSuccess = () => {
        queryClient.invalidateQueries('vehicles');
        queryClient.invalidateQueries('vehicleCatalogMakes');
        queryClient.invalidateQueries('vehicleCatalogModels');
        queryClient.invalidateQueries(['vehicleDocuments', createdVehicle.id]);
        toast.success(t('fleet.messages.created'));
        setIsCreating(false);
        setEditMode(false);
        setIsDirty(false);
        resetForm();
      };

      const uploadRegistrationDocThenFinalize = () => {
        if (!docFile) {
          finalizeCreateSuccess();
          return;
        }
        registrationFileRef.current = null;
        vehicleApi
          .uploadDocument(createdVehicle.id.toString(), docFile)
          .catch((err: any) => {
            toast.error(err.response?.data?.error || err.message || t('fleet.messages.uploadFailed'));
          })
          .finally(finalizeCreateSuccess);
      };

      const file = pendingImageFileRef.current;
      if (file) {
        pendingImageFileRef.current = null;
        setPendingImageFile(null);
        const previewUrl = pendingImagePreviewRef.current;
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          pendingImagePreviewRef.current = null;
          setPendingImagePreview(null);
        }
        uploadChainedFromCreateRef.current = true;
        imageUploadMutation.mutate(
          { id: createdVehicle.id.toString(), file },
          {
            onSuccess: uploadRegistrationDocThenFinalize,
            onError: (err: any) => {
              toast.error(err.response?.data?.error || err.message || t('fleet.messages.uploadFailed'));
              uploadRegistrationDocThenFinalize();
            },
          }
        );
      } else {
        uploadRegistrationDocThenFinalize();
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message || t('fleet.messages.createFailed'));
    },
  });

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Vehicle> }) => vehicleApi.update(id, data),
    {
      onSuccess: (updatedVehicle) => {
        queryClient.invalidateQueries('vehicles');
        queryClient.invalidateQueries('vehicleCatalogMakes');
        queryClient.invalidateQueries('vehicleCatalogModels');
        toast.success(t('fleet.messages.updated'));
        if (isReviewing) {
          handleReviewAdvance();
        } else {
          loadVehicleIntoEditor(updatedVehicle);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || error.message || t('fleet.messages.updateFailed'));
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(vehicleApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('vehicles');
      toast.success(t('fleet.messages.deleted'));
      setOpenDeleteDialog(false);
      setOpenDocumentsDialog(false);
      setSelectedDocumentFile(null);
      setSelectedVehicle(null);
      setEditMode(false);
      setIsDirty(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message || t('fleet.messages.deleteFailed'));
    },
  });

  // Image upload mutation
  const imageUploadMutation = useMutation(
    ({ id, file }: { id: string; file: File }) => vehicleApi.uploadImage(id, file),
    {
      onSuccess: (updatedVehicle) => {
        queryClient.invalidateQueries('vehicles');
        if (selectedVehicle && selectedVehicle.id === updatedVehicle.id) {
          setSelectedVehicle(updatedVehicle);
          setFormData({ ...formData, imageUrl: updatedVehicle.imageUrls?.[0] || '' });
        }
        if (!uploadChainedFromCreateRef.current) {
          toast.success(t('fleet.messages.imageUploaded'));
        }
        uploadChainedFromCreateRef.current = false;
      },
      onError: (error: any) => {
        uploadChainedFromCreateRef.current = false;
        toast.error(error.response?.data?.error || error.message || t('fleet.messages.uploadFailed'));
      },
    }
  );

  const extractRegistrationMutation = useMutation(documentApi.uploadAndExtract, {
    onSuccess: (response) => {
      const fields = response.extraction?.fields || [];
      const valuesByField = new Map(fields.map((field) => [field.fieldName, field.normalizedValue || field.fieldValue]));
      const filledFields = new Set<string>();

      setFormData((current) => {
        const next = { ...current };
        const applyText = (field: keyof VehicleFormData) => {
          const value = valuesByField.get(field as string);
          if (value) {
            (next as any)[field] = value;
            filledFields.add(field as string);
          }
        };
        const applyInt = (field: keyof VehicleFormData) => {
          const value = valuesByField.get(field as string);
          const parsed = value ? parseInt(value, 10) : NaN;
          if (Number.isFinite(parsed)) {
            (next as any)[field] = parsed;
            filledFields.add(field as string);
          }
        };

        applyText('make');
        applyText('model');
        applyInt('year');
        applyText('licensePlate');
        applyText('vin');
        applyText('color');
        applyText('fuelType');
        applyInt('seatingCapacity');
        return next;
      });

      // The Make/Model Autocompletes render their own inputValue state, not formData directly,
      // so it must be updated too or the boxes appear blank despite formData holding the value.
      const extractedMake = valuesByField.get('make');
      if (extractedMake) {
        setSelectedMakeId(null);
        setMakeSearchInput(extractedMake);
      }
      const extractedModel = valuesByField.get('model');
      if (extractedModel) {
        setModelSearchInput(extractedModel);
      }

      setAiFilledFields(filledFields);

      if (response.processingStatus === 'COMPLETED') {
        toast.success(t('fleet.registration.extractSuccess', { defaultValue: 'Registration details extracted. Please review before saving.' }));
      } else if (response.processingStatus === 'SKIPPED') {
        toast(response.extraction?.failureReason || t('fleet.registration.extractSkipped', { defaultValue: 'AI extraction is not enabled for this company.' }));
      } else {
        toast.error(response.extraction?.failureReason || t('fleet.registration.extractFailed', { defaultValue: 'Could not read the registration document.' }));
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message || t('fleet.registration.extractFailed', { defaultValue: 'Could not read the registration document.' }));
    },
  });

  const uploadDocumentMutation = useMutation(
    ({ id, file }: { id: string; file: File }) => vehicleApi.uploadDocument(id, file),
    {
      onSuccess: () => {
        if (selectedVehicle) {
          queryClient.invalidateQueries(['vehicleDocuments', selectedVehicle.id]);
        }
        setSelectedDocumentFile(null);
        toast.success(t('fleet.documents.uploadSuccess', { defaultValue: 'Document uploaded successfully' }));
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            error.message ||
            t('fleet.documents.uploadFailed', { defaultValue: 'Failed to upload vehicle document' })
        );
      },
    }
  );

  const deleteDocumentMutation = useMutation(
    ({ vehicleId, documentId }: { vehicleId: string; documentId: string }) =>
      vehicleApi.deleteDocument(vehicleId, documentId),
    {
      onSuccess: () => {
        if (selectedVehicle) {
          queryClient.invalidateQueries(['vehicleDocuments', selectedVehicle.id]);
        }
        toast.success(t('fleet.documents.deleteSuccess', { defaultValue: 'Document deleted successfully' }));
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            error.message ||
            t('fleet.documents.deleteFailed', { defaultValue: 'Failed to delete vehicle document' })
        );
      },
    }
  );

  // Clear image mutation (for existing vehicles)
  const clearImageMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Vehicle> }) => vehicleApi.update(id, data),
    {
      onSuccess: (updatedVehicle) => {
        queryClient.invalidateQueries('vehicles');
        if (selectedVehicle && selectedVehicle.id === updatedVehicle.id) {
          setSelectedVehicle(updatedVehicle);
          setFormData({ ...formData, imageUrl: '' });
        }
        toast.success(t('fleet.messages.imageRemoved'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || error.message || t('fleet.messages.removeImageFailed'));
      },
    }
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedMakeSearch(makeSearchInput.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [makeSearchInput]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedModelSearch(modelSearchInput.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [modelSearchInput]);

  const resetForm = () => {
    setFormData(createDefaultFormData(t));
    setSelectedVehicle(null);
    setSelectedDocumentFile(null);
    setOpenDocumentsDialog(false);
    setSelectedMakeId(null);
    setMakeSearchInput('');
    setModelSearchInput('');
    setDebouncedMakeSearch('');
    setDebouncedModelSearch('');
    setIsCreating(false);
    setEditMode(false);
    setIsDirty(false);
    if (pendingImageFile) setPendingImageFile(null);
    if (pendingImagePreview) {
      URL.revokeObjectURL(pendingImagePreview);
      setPendingImagePreview(null);
    }
    pendingImageFileRef.current = null;
    pendingImagePreviewRef.current = null;
    setRegistrationFile(null);
    setAiFilledFields(new Set());
    setReviewQueue([]);
    setReviewIndex(0);
  };

  const handleNewVehicle = () => {
    if (isDirty) {
      setPendingAction(() => () => {
        resetForm();
        setIsCreating(true);
        setEditMode(true);
      });
      setShowUnsavedDialog(true);
      return;
    }
    resetForm();
    setIsCreating(true);
    setEditMode(true);
  };

  const handleRowClick = (vehicle: Vehicle) => {
    if (isDirty) {
      setPendingAction(() => () => {
        handleRowClickInternal(vehicle);
      });
      setShowUnsavedDialog(true);
      return;
    }
    handleRowClickInternal(vehicle);
  };

  const handleRowClickInternal = (vehicle: Vehicle) => {
    if (selectedVehicle?.id === vehicle.id && !isCreating) {
      return;
    }

    setReviewQueue([]);
    setReviewIndex(0);
    loadVehicleIntoEditor(vehicle);
  };

  const makeAutocompleteValue: VehicleCatalogMake | string | null = formData.make
    ? makeOptions.find((option) => option.name.toLowerCase() === formData.make.toLowerCase()) ?? formData.make
    : null;

  const modelAutocompleteValue: VehicleCatalogModel | string | null = formData.model
    ? modelOptions.find((option) => option.name.toLowerCase() === formData.model.toLowerCase()) ?? formData.model
    : null;

  const handleMakeSelectionChange = (value: VehicleCatalogMake | string | null) => {
    const nextMakeName = typeof value === 'string' ? value : value?.name || '';
    const nextMakeId = typeof value === 'string' ? null : value?.id ?? null;
    const makeChanged = nextMakeName !== formData.make;

    setSelectedMakeId(nextMakeId);
    setMakeSearchInput(nextMakeName);
    setFormData({
      ...formData,
      make: nextMakeName,
      model: makeChanged ? '' : formData.model,
    });
    clearAiFilledField('make');
    if (makeChanged) {
      setModelSearchInput('');
      clearAiFilledField('model');
    }
    setIsDirty(true);
  };

  const handleMakeInputChange = (value: string) => {
    setMakeSearchInput(value);
    setSelectedMakeId(null);
    if (value !== formData.make) {
      setFormData({
        ...formData,
        make: value,
        model: '',
      });
      setModelSearchInput('');
      clearAiFilledField('make');
      clearAiFilledField('model');
      setIsDirty(true);
    }
  };

  const handleModelSelectionChange = (value: VehicleCatalogModel | string | null) => {
    const nextModelName = typeof value === 'string' ? value : value?.name || '';
    setModelSearchInput(nextModelName);
    setFormData({
      ...formData,
      model: nextModelName,
    });
    clearAiFilledField('model');
    setIsDirty(true);
  };

  const handleModelInputChange = (value: string) => {
    setModelSearchInput(value);
    if (value !== formData.model) {
      setFormData({
        ...formData,
        model: value,
      });
      clearAiFilledField('model');
      setIsDirty(true);
    }
  };

  const handleDelete = () => {
    if (selectedVehicle) {
      setOpenDeleteDialog(true);
    }
  };

  const handleOpenDocumentsDialog = () => {
    if (!selectedVehicle) {
      return;
    }
    setSelectedDocumentFile(null);
    setOpenDocumentsDialog(true);
  };

  const handleCloseDocumentsDialog = () => {
    setSelectedDocumentFile(null);
    setOpenDocumentsDialog(false);
  };

  const handleConfirmDelete = () => {
    if (selectedVehicle) {
      deleteMutation.mutate(selectedVehicle.id.toString());
    }
  };

  const handleSave = () => {
    if (!formData.make || !formData.model || !formData.licensePlate) {
      toast.error(t('fleet.validation.required'));
      return;
    }

    if (isReviewing && formData.licensePlate.trim().toUpperCase().startsWith('PENDING-')) {
      toast.error(
        t('fleet.bulkImport.plateRequired', { defaultValue: 'Enter the real license plate before confirming.' })
      );
      return;
    }

    const payload = buildVehiclePayload(isReviewing ? { pendingReview: false } : {});

    if (isCreating) {
      createMutation.mutate(payload);
    } else if (editMode && selectedVehicle) {
      updateMutation.mutate({ id: selectedVehicle.id.toString(), data: payload });
    }
  };

  const handleImageFile = (file: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('fleet.validation.imageSize'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error(t('fleet.validation.imageType'));
      return;
    }
    if (isCreating) {
      setPendingImageFile(file);
      if (pendingImagePreviewRef.current) URL.revokeObjectURL(pendingImagePreviewRef.current);
      const url = URL.createObjectURL(file);
      pendingImagePreviewRef.current = url;
      setPendingImagePreview(url);
      setIsDirty(true);
    } else if (selectedVehicle) {
      imageUploadMutation.mutate({ id: selectedVehicle.id.toString(), file });
    }
  };

  const handleClearImage = () => {
    if (isCreating) {
      setPendingImageFile(null);
      if (pendingImagePreviewRef.current) {
        URL.revokeObjectURL(pendingImagePreviewRef.current);
        pendingImagePreviewRef.current = null;
      }
      setPendingImagePreview(null);
      setFormData({ ...formData, imageUrl: '' });
      setIsDirty(true);
    } else if (selectedVehicle) {
      clearImageMutation.mutate({
        id: selectedVehicle.id.toString(),
        data: buildVehiclePayload({ imageUrl: '' }),
      });
    }
  };

  const handleRegistrationFile = (file: File) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('fleet.registration.fileTooLarge', { defaultValue: 'Document size must be less than 10MB' }));
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('fleet.registration.invalidFileType', { defaultValue: 'Upload a PDF, JPEG, PNG, or WEBP file' }));
      return;
    }
    setRegistrationFile(file);
  };

  const handleExtractRegistration = () => {
    if (!registrationFile) return;
    extractRegistrationMutation.mutate({
      documentType: 'VEHICLE_REGISTRATION',
      frontFile: registrationFile,
    });
  };

  const handleImportFilesSelected = (files: File[]) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('fleet.registration.fileTooLarge', { defaultValue: 'Document size must be less than 10MB' }) + `: ${file.name}`);
        continue;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error(t('fleet.registration.invalidFileType', { defaultValue: 'Upload a PDF, JPEG, PNG, or WEBP file' }) + `: ${file.name}`);
        continue;
      }
      validFiles.push(file);
    }
    setImportFiles((current) => [...current, ...validFiles]);
  };

  const handleRemoveImportFile = (index: number) => {
    setImportFiles((current) => current.filter((_, i) => i !== index));
  };

  const IMPORT_CONCURRENCY = 2;

  const handleStartImport = async () => {
    if (importFiles.length === 0 || importRunning) return;
    setImportRunning(true);
    const files = importFiles;
    const items: ImportItem[] = files.map((file) => ({ file, status: 'queued' }));
    setImportItems(items);

    let nextIndex = 0;
    const runWorker = async () => {
      while (nextIndex < files.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        setImportItems((prev) => prev.map((item, i) => (i === currentIndex ? { ...item, status: 'processing' } : item)));
        try {
          const extraction = await documentApi.uploadAndExtract({
            documentType: 'VEHICLE_REGISTRATION',
            frontFile: files[currentIndex],
          });
          const valuesByField = new Map(
            (extraction.extraction?.fields || []).map((field) => [field.fieldName, field.normalizedValue || field.fieldValue])
          );
          const toInt = (value: string | undefined) => {
            const parsed = value ? parseInt(value, 10) : NaN;
            return Number.isFinite(parsed) ? parsed : undefined;
          };
          const draftVehicle = await vehicleApi.bulkImportDraft({
            make: valuesByField.get('make'),
            model: valuesByField.get('model'),
            year: toInt(valuesByField.get('year')),
            licensePlate: valuesByField.get('licensePlate'),
            vin: valuesByField.get('vin'),
            color: valuesByField.get('color'),
            fuelType: valuesByField.get('fuelType'),
            seatingCapacity: toInt(valuesByField.get('seatingCapacity')),
          });
          try {
            await vehicleApi.uploadDocument(draftVehicle.id.toString(), files[currentIndex]);
          } catch {
            // Best-effort: the vehicle draft was created successfully even if attaching the source document failed.
          }
          setImportItems((prev) =>
            prev.map((item, i) => (i === currentIndex ? { ...item, status: 'done', vehicleId: draftVehicle.id } : item))
          );
        } catch (error: any) {
          setImportItems((prev) =>
            prev.map((item, i) =>
              i === currentIndex
                ? { ...item, status: 'failed', error: error.response?.data?.error || error.message || t('common.failed', 'Failed') }
                : item
            )
          );
        }
      }
    };

    const workerCount = Math.min(IMPORT_CONCURRENCY, files.length);
    await Promise.all(Array.from({ length: workerCount }, runWorker));

    setImportRunning(false);
    setImportFiles([]);
    queryClient.invalidateQueries('vehicles');
  };

  const loadReviewVehicleIntoEditor = (vehicle: Vehicle) => {
    loadVehicleIntoEditor(vehicle);
    // Default to available once reviewed; the user can still uncheck it before confirming.
    setFormData((current) => ({ ...current, isAvailableForRental: true }));
  };

  const handleStartReview = () => {
    if (pendingReviewVehicles.length === 0) return;
    const ids = pendingReviewVehicles.map((vehicle) => vehicle.id);
    setReviewQueue(ids);
    setReviewIndex(0);
    setImportDialogOpen(false);
    if (isDirty) {
      setPendingAction(() => () => loadReviewVehicleIntoEditor(pendingReviewVehicles[0]));
      setShowUnsavedDialog(true);
      return;
    }
    loadReviewVehicleIntoEditor(pendingReviewVehicles[0]);
  };

  const handleReviewAdvance = () => {
    const nextIndex = reviewIndex + 1;
    if (nextIndex >= reviewQueue.length) {
      setReviewQueue([]);
      setReviewIndex(0);
      resetForm();
      return;
    }
    const nextId = reviewQueue[nextIndex];
    const nextVehicle = vehicles.find((vehicle) => vehicle.id === nextId);
    setReviewIndex(nextIndex);
    if (nextVehicle) {
      loadReviewVehicleIntoEditor(nextVehicle);
    } else {
      handleReviewAdvance();
    }
  };

  const handleSkipReview = () => {
    handleReviewAdvance();
  };

  const handleOpenImportDialog = () => {
    setImportFiles([]);
    setImportItems([]);
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    if (importRunning) return;
    setImportDialogOpen(false);
    setImportFiles([]);
    setImportItems([]);
  };

  const handleUploadDocument = () => {
    if (!selectedVehicle || !selectedDocumentFile) {
      return;
    }

    if (selectedDocumentFile.size > 10 * 1024 * 1024) {
      toast.error(t('fleet.documents.fileTooLarge', { defaultValue: 'Document size must be less than 10MB' }));
      return;
    }

    uploadDocumentMutation.mutate({
      id: selectedVehicle.id.toString(),
      file: selectedDocumentFile,
    });
  };

  const handlePreviewDocument = async (document: VehicleDocument) => {
    if (!selectedVehicle) {
      return;
    }

    try {
      const blob = await vehicleApi.previewDocument(selectedVehicle.id.toString(), document.id.toString());
      const fileName = safeFilePart(document.originalFileName, `vehicle-document-${document.id}`);
      const url = URL.createObjectURL(blob);
      const previewWindow = window.open(url, '_blank', 'noopener,noreferrer');

      if (!previewWindow) {
        const link = window.document.createElement('a');
        link.href = url;
        link.download = fileName;
        window.document.body.appendChild(link);
        link.click();
        link.remove();
      }

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          error.message ||
          t('fleet.documents.previewFailed', { defaultValue: 'Failed to preview vehicle document' })
      );
    }
  };

  const handleDownloadDocument = async (document: VehicleDocument) => {
    if (!selectedVehicle) {
      return;
    }

    try {
      const blob = await vehicleApi.downloadDocument(selectedVehicle.id.toString(), document.id.toString());
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = safeFilePart(document.originalFileName, `vehicle-document-${document.id}`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          error.message ||
          t('fleet.documents.downloadFailed', { defaultValue: 'Failed to download vehicle document' })
      );
    }
  };

  const handleDeleteDocument = (document: VehicleDocument) => {
    if (!selectedVehicle) {
      return;
    }

    deleteDocumentMutation.mutate({
      vehicleId: selectedVehicle.id.toString(),
      documentId: document.id.toString(),
    });
  };

  // Handle column filter change
  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters({ ...columnFilters, [column]: value });
    setPage(0);
  };

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return current.direction === 'asc' 
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  };

  // Filter vehicles with column-level filters
  let filteredVehicles = vehicles.filter((vehicle) => {
    // Column filters
    const matchesVehicle = 
      columnFilters.vehicle === '' ||
      `${vehicle.make} ${vehicle.model}`.toLowerCase().includes(columnFilters.vehicle.toLowerCase());
    
    const matchesYear = 
      columnFilters.year === '' ||
      vehicle.year.toString().includes(columnFilters.year);
    
    const matchesLicensePlate = 
      columnFilters.licensePlate === '' ||
      (vehicle.licensePlate || '').toLowerCase().includes(columnFilters.licensePlate.toLowerCase());
    
    const matchesType = 
      columnFilters.type === '' ||
      vehicle.type.toLowerCase().includes(columnFilters.type.toLowerCase()) ||
      vehicleTypeLabel(t, vehicle.type).toLowerCase().includes(columnFilters.type.toLowerCase());

    const matchesVehicleClass =
      columnFilters.vehicleClass === '' ||
      (vehicle.vehicleClass || '').toLowerCase().includes(columnFilters.vehicleClass.toLowerCase()) ||
      vehicleClassLabel(t, vehicle.vehicleClass).toLowerCase().includes(columnFilters.vehicleClass.toLowerCase());
    
    const matchesStatus = 
      columnFilters.status === '' ||
      vehicle.status.toLowerCase().includes(columnFilters.status.toLowerCase()) ||
      statusLabel(t, vehicle.status).toLowerCase().includes(columnFilters.status.toLowerCase());
    
    const matchesDailyRate = 
      columnFilters.dailyRate === '' ||
      (typeof vehicle.dailyRate === 'string' ? parseFloat(vehicle.dailyRate) : vehicle.dailyRate || 0)
        .toString().includes(columnFilters.dailyRate);
    
    const matchesMileage = 
      columnFilters.mileage === '' ||
      (vehicle.mileage || 0).toString().includes(columnFilters.mileage);
    
    const matchesLocation = 
      columnFilters.location === '' ||
      (vehicle.currentLocation || '').toLowerCase().includes(columnFilters.location.toLowerCase());
    
    return matchesVehicle && matchesYear && matchesLicensePlate && 
           matchesType && matchesVehicleClass && matchesStatus && matchesDailyRate && matchesMileage && matchesLocation;
  });

  // Apply sorting
  if (sortConfig) {
    filteredVehicles = [...filteredVehicles].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case 'vehicle':
          aValue = `${a.make} ${a.model}`.toLowerCase();
          bValue = `${b.make} ${b.model}`.toLowerCase();
          break;
        case 'year':
          aValue = a.year;
          bValue = b.year;
          break;
        case 'licensePlate':
          aValue = (a.licensePlate || '').toLowerCase();
          bValue = (b.licensePlate || '').toLowerCase();
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'vehicleClass':
          aValue = a.vehicleClass || '';
          bValue = b.vehicleClass || '';
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'dailyRate':
          aValue = typeof a.dailyRate === 'string' ? parseFloat(a.dailyRate) : a.dailyRate || 0;
          bValue = typeof b.dailyRate === 'string' ? parseFloat(b.dailyRate) : b.dailyRate || 0;
          break;
        case 'mileage':
          aValue = a.mileage || 0;
          bValue = b.mileage || 0;
          break;
        case 'location':
          aValue = (a.currentLocation || '').toLowerCase();
          bValue = (b.currentLocation || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const paginatedVehicles = filteredVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status: VehicleStatus) => {
    switch (status) {
      case VehicleStatus.AVAILABLE:
        return 'success';
      case VehicleStatus.RENTED:
        return 'info';
      case VehicleStatus.MAINTENANCE:
        return 'warning';
      case VehicleStatus.OUT_OF_SERVICE:
        return 'error';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }

    return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatDocumentCreatedAt = (value?: string) => {
    if (!value) {
      return t('common.unavailable');
    }
    return formatDateTime(value);
  };

  const handleDiscardChanges = () => {
    setIsDirty(false);
    setShowUnsavedDialog(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleKeepEditing = () => {
    setShowUnsavedDialog(false);
    setPendingAction(null);
  };

  return (
    <Box>
      {/* Sticky Toolbar with New, Save, Delete buttons - Connected to top of page */}
      <Box
        sx={{ 
          position: 'sticky',
          top: 64, // Position below AppBar (AppBar height is typically 64px)
          zIndex: 1100, // Higher than AppBar (which is 1100) to stay on top
          bgcolor: 'background.paper', 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%'
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar 
            sx={{ 
              pl: { xs: 2, sm: 3 }, 
              pr: { xs: 2, sm: 3 }, 
              minHeight: '48px !important', 
              py: 0.5
            }}
          >
            {/* Action Buttons - New, Save, Delete */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewVehicle}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                {t('common.new')}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!isCreating && !editMode}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  opacity: (isCreating || editMode) ? 1 : 0.6,
                  '&.Mui-disabled': {
                    opacity: 0.4
                  }
                }}
              >
                {isReviewing
                  ? reviewIndex + 1 >= reviewQueue.length
                    ? t('fleet.bulkImport.confirmFinish', { defaultValue: 'Confirm & Finish' })
                    : t('fleet.bulkImport.confirmNext', { defaultValue: 'Confirm & Next' })
                  : t('common.save')}
              </Button>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={!selectedVehicle}
                color="error"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                {t('common.delete')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                onClick={handleOpenDocumentsDialog}
                disabled={!selectedVehicle}
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                {t('fleet.documents.button', { defaultValue: 'Upload document' })}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={handleOpenImportDialog}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                {t('fleet.bulkImport.button', { defaultValue: 'Import' })}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        {pendingReviewVehicles.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 1 }}
            action={
              <Button color="inherit" size="small" onClick={handleStartReview}>
                {t('fleet.bulkImport.reviewNow', { defaultValue: 'Review Now' })}
              </Button>
            }
          >
            {t('fleet.bulkImport.pendingReviewBanner', {
              count: pendingReviewVehicles.length,
              defaultValue: '{{count}} vehicle(s) imported and waiting for review before they go live.',
            })}
          </Alert>
        )}
        <Grid container spacing={0}>
          {/* Main Table Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              {/* Secondary Toolbar - Refresh, Export, Print, Pagination, Clear Filters */}
              <Toolbar 
                sx={{ 
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pl: { xs: 2, sm: 3 }, 
                  pr: { xs: 2, sm: 3 }, 
                  minHeight: '48px !important', 
                  py: 0.5,
                  gap: 0.5
                }}
              >
                {/* Secondary Action Buttons - Left */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh fontSize="small" />}
                    onClick={() => queryClient.invalidateQueries('vehicles')}
                    size="small"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '& .MuiButton-startIcon': {
                        marginRight: 0.5
                      }
                    }}
                  >
                    {t('common.refresh')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Print fontSize="small" />}
                    size="small"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '& .MuiButton-startIcon': {
                        marginRight: 0.5
                      }
                    }}
                  >
                    {t('common.print')}
                  </Button>
                </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Pagination Controls - Center (like Logistiqo) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Left Navigation Arrow */}
                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  sx={{ 
                    width: '24px',
                    height: '24px',
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowUpward sx={{ fontSize: '0.875rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
                
                {/* Page Info */}
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', mx: 1 }}>
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredVehicles.length)} {t('common.of')} {filteredVehicles.length}
                </Typography>
                
                {/* Page Numbers */}
                {(() => {
                  const totalPages = Math.ceil(filteredVehicles.length / rowsPerPage);
                  const pages: (number | 'ellipsis')[] = [];
                  
                  // Always show first page
                  if (page > 3) {
                    pages.push(1);
                    if (page > 4) {
                      pages.push('ellipsis');
                    }
                  }
                  
                  // Show pages around current page
                  const start = Math.max(1, page - 1);
                  const end = Math.min(totalPages, page + 3);
                  
                  for (let i = start; i <= end; i++) {
                    if (i !== 1 || page <= 3) { // Don't duplicate page 1
                      pages.push(i);
                    }
                  }
                  
                  // Add ellipsis and last page if needed
                  if (page + 3 < totalPages - 1) {
                    pages.push('ellipsis');
                  }
                  if (page + 3 < totalPages) {
                    pages.push(totalPages);
                  }
                  
                  return pages.map((item, idx) => {
                    if (item === 'ellipsis') {
                      return (
                        <Typography key={`ellipsis-${idx}`} variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}>
                          ...
                        </Typography>
                      );
                    }
                    
                    const pageNum = item - 1; // Convert to 0-based
                    const isCurrent = pageNum === page;
                    
                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: '24px',
                          height: '24px',
                          px: 0.75,
                          fontSize: '0.8125rem',
                          bgcolor: isCurrent ? 'primary.main' : 'transparent',
                          color: isCurrent ? 'white' : 'text.primary',
                          fontWeight: isCurrent ? 600 : 400,
                          '&:hover': {
                            bgcolor: isCurrent ? 'primary.dark' : 'action.hover'
                          }
                        }}
                      >
                        {item}
                      </Button>
                    );
                  });
                })()}
                
                {/* Right Navigation Arrow */}
                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.min(Math.ceil(filteredVehicles.length / rowsPerPage) - 1, prev + 1))}
                  disabled={page >= Math.ceil(filteredVehicles.length / rowsPerPage) - 1}
                  sx={{ 
                    width: '24px',
                    height: '24px',
                    ml: 0.5,
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowDownward sx={{ fontSize: '0.875rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
              </Box>

              {/* Rows per page dropdown - Right side (like Logistiqo) */}
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                size="small"
                  sx={{ 
                  height: '24px',
                  fontSize: '0.8125rem',
                  minWidth: '70px',
                  '& .MuiSelect-select': { 
                    py: 0.5, 
                    px: 1,
                      display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  }
                }}
                renderValue={(value) => t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Clear Filters Button - Right */}
              <Button
                size="small"
                startIcon={<FilterListOff />}
                onClick={() => {
                  setColumnFilters({
                    vehicle: '',
                    year: '',
                    licensePlate: '',
                    type: '',
                    vehicleClass: '',
                    status: '',
                    dailyRate: '',
                    mileage: '',
                    location: '',
                  });
                  setSortConfig(null);
                  setPage(0);
                }}
                sx={{ fontSize: '0.8125rem' }}
              >
                {t('common.clearFilters')}
              </Button>
            </Toolbar>

            {/* Table */}
            <TableContainer>
              <Table 
                size="small" 
                sx={{ 
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { 
                    fontSize: '0.875rem',
                    px: { xs: 2, sm: 3 }
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    {columnDefs.map((column) => (
                      <TableFilterCell
                        key={column.key}
                        placeholder={column.label}
                        value={columnFilters[column.key]}
                        onChange={(value) => handleColumnFilterChange(column.key, value)}
                        onSort={() => handleSort(column.key)}
                        sortDirection={sortConfig?.key === column.key ? sortConfig.direction : null}
                        width={column.width}
                        cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : paginatedVehicles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                        {t('management.emptyVehicles')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedVehicles.map((vehicle) => (
                      <TableRow
                        key={vehicle.id}
                        hover
                        selected={selectedVehicle?.id === vehicle.id}
                        onClick={() => handleRowClick(vehicle)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          '&.Mui-selected': { bgcolor: 'action.selected' }
                        }}
                      >
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '200px',
                          width: '200px'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                            {vehicle.imageUrls && vehicle.imageUrls.length > 0 ? (
                              <Avatar
                                src={vehicle.imageUrls[0]}
                                sx={{ width: 32, height: 32, borderRadius: 0.5, flexShrink: 0 }}
                                variant="rounded"
                              >
                                <DirectionsCar sx={{ fontSize: '1rem' }} />
                              </Avatar>
                            ) : (
                              <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32, borderRadius: 0.5, flexShrink: 0 }} variant="rounded">
                                <DirectionsCar sx={{ fontSize: '0.875rem' }} />
                              </Avatar>
                            )}
                            <Typography 
                              variant="body2" 
                              fontWeight={500} 
                              sx={{ 
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                flex: 1
                              }}
                            >
                              {vehicle.make} {vehicle.model}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '100px',
                          width: '100px'
                        }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {vehicle.year}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '130px',
                          width: '130px'
                        }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {vehicle.licensePlate || '-'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '120px',
                          width: '120px'
                        }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {vehicleTypeLabel(t, vehicle.type)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '120px',
                          width: '120px'
                        }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {vehicleClassLabel(t, vehicle.vehicleClass)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '120px',
                          width: '120px'
                        }}>
                          <Chip
                            label={statusLabel(t, vehicle.status)}
                            size="small"
                            color={getStatusColor(vehicle.status) as any}
                            sx={{ 
                              height: '20px',
                              fontSize: '0.75rem',
                              maxWidth: '100%',
                              '& .MuiChip-label': { 
                                px: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }
                            }}
                          />
                          {vehicle.pendingReview && (
                            <Chip
                              label={t('fleet.bulkImport.needsReview', { defaultValue: 'Needs Review' })}
                              size="small"
                              color="warning"
                              sx={{ height: '20px', fontSize: '0.7rem', mt: 0.5, display: 'block', width: 'fit-content' }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{
                          py: 0.75,
                          px: 1.5,
                          fontSize: '0.875rem',
                          maxWidth: '130px',
                          width: '130px'
                        }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {t('common.perDay', { value: formatCurrency(typeof vehicle.dailyRate === 'string' ? parseFloat(vehicle.dailyRate) : vehicle.dailyRate || 0) })}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '120px',
                          width: '120px'
                        }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {(vehicle.mileage || 0).toLocaleString()} km
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 0.75, 
                          px: 1.5, 
                          fontSize: '0.875rem',
                          maxWidth: '150px',
                          width: '150px'
                        }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {vehicle.currentLocation || '-'}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Detail Panel */}
        {(isCreating || selectedVehicle) && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ mt: 2, p: 1, display: 'flex', flexDirection: 'column', ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">
                  {isCreating
                    ? t('fleet.newVehicle')
                    : t('fleet.vehicleHeading', { vehicle: `${selectedVehicle?.make} ${selectedVehicle?.model}` })}
                </Typography>
                {selectedVehicle && !isCreating && (
                  <Chip
                    label={statusLabel(t, selectedVehicle.status)}
                    size="small"
                    color={getStatusColor(selectedVehicle.status) as any}
                  />
                )}
                {isReviewing && (
                  <Chip
                    label={t('fleet.bulkImport.reviewingChip', {
                      current: reviewIndex + 1,
                      total: reviewQueue.length,
                      defaultValue: `Reviewing ${reviewIndex + 1} of ${reviewQueue.length}`,
                    })}
                    size="small"
                    color="warning"
                  />
                )}
                {isReviewing && (
                  <Button size="small" onClick={handleSkipReview} sx={{ textTransform: 'none' }}>
                    {t('fleet.bulkImport.skip', { defaultValue: 'Skip for now' })}
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={5}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        freeSolo
                        fullWidth
                        size="small"
                        options={makeOptions}
                        loading={isLoadingMakeOptions}
                        value={makeAutocompleteValue}
                        inputValue={makeSearchInput}
                        onChange={(_, value) => handleMakeSelectionChange(value)}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            handleMakeInputChange(value);
                          }
                          if (reason === 'clear') {
                            handleMakeSelectionChange(null);
                          }
                        }}
                        filterOptions={(options) => options}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                        isOptionEqualToValue={(option, value) =>
                          typeof value !== 'string' && option.name.toLowerCase() === value.name.toLowerCase()
                        }
                        noOptionsText={t('fleet.catalog.noMakes')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label={t('fields.make')}
                            required
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoadingMakeOptions ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        freeSolo
                        fullWidth
                        size="small"
                        options={modelOptions}
                        loading={isLoadingModelOptions}
                        value={modelAutocompleteValue}
                        inputValue={modelSearchInput}
                        disabled={!formData.make.trim()}
                        onChange={(_, value) => handleModelSelectionChange(value)}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            handleModelInputChange(value);
                          }
                          if (reason === 'clear') {
                            handleModelSelectionChange(null);
                          }
                        }}
                        filterOptions={(options) => options}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                        isOptionEqualToValue={(option, value) =>
                          typeof value !== 'string' && option.name.toLowerCase() === value.name.toLowerCase()
                        }
                        noOptionsText={formData.make.trim() ? t('fleet.catalog.noModels') : t('fleet.catalog.selectMakeFirst')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label={t('fields.model')}
                            required
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoadingModelOptions ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.year')}
                        type="number"
                        value={formData.year}
                        onChange={(e) => {
                          setFormData({ ...formData, year: parseInt(e.target.value) || new Date().getFullYear() });
                          clearAiFilledField('year');
                          setIsDirty(true);
                        }}
                        sx={aiFilledFields.has('year') ? aiFilledFieldSx : undefined}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.plate')}
                        value={formData.licensePlate}
                        onChange={(e) => {
                          setFormData({ ...formData, licensePlate: e.target.value });
                          clearAiFilledField('licensePlate');
                          setIsDirty(true);
                        }}
                        sx={aiFilledFields.has('licensePlate') ? aiFilledFieldSx : undefined}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.vin', 'VIN / Chassis No.')}
                        value={formData.vin}
                        onChange={(e) => {
                          setFormData({ ...formData, vin: e.target.value });
                          setIsDirty(true);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" sx={aiFilledFields.has('fuelType') ? aiFilledFieldSx : undefined}>
                        <InputLabel>{t('fields.fuel')}</InputLabel>
                        <Select
                          value={formData.fuelType}
                          label={t('fields.fuel')}
                          onChange={(e) => {
                            setFormData({ ...formData, fuelType: e.target.value });
                            clearAiFilledField('fuelType');
                            setIsDirty(true);
                          }}
                          MenuProps={{ disableScrollLock: true }}
                          sx={{ '& .MuiSelect-select': { py: 1 } }}
                        >
                          <MenuItem value="Diesel">{fuelTypeLabel(t, 'Diesel')}</MenuItem>
                          <MenuItem value="Petrol">{fuelTypeLabel(t, 'Petrol')}</MenuItem>
                          <MenuItem value="Hybrid">{fuelTypeLabel(t, 'Hybrid')}</MenuItem>
                          <MenuItem value="Petrol-LPG">{fuelTypeLabel(t, 'Petrol-LPG')}</MenuItem>
                          <MenuItem value="LPG">{fuelTypeLabel(t, 'LPG')}</MenuItem>
                          <MenuItem value="Electric">{fuelTypeLabel(t, 'Electric')}</MenuItem>
                          <MenuItem value="PHEV">{fuelTypeLabel(t, 'PHEV')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('fields.transmission')}</InputLabel>
                        <Select
                          value={formData.transmission}
                          label={t('fields.transmission')}
                          onChange={(e) => {
                            setFormData({ ...formData, transmission: e.target.value });
                            setIsDirty(true);
                          }}
                          MenuProps={{ disableScrollLock: true }}
                          sx={{ '& .MuiSelect-select': { py: 1 } }}
                        >
                          <MenuItem value="Automatic">{t('fleet.transmissionAutomatic', 'Automatic')}</MenuItem>
                          <MenuItem value="Manual">{t('fleet.transmissionManual', 'Manual')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.engineSize')}
                        placeholder={t('fleet.engineSizePlaceholder', 'e.g. 1.6L, 2000cc')}
                        value={formData.engineSize}
                        onChange={(e) => {
                          setFormData({ ...formData, engineSize: e.target.value });
                          setIsDirty(true);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.seats')}
                        type="number"
                        value={formData.seatingCapacity}
                        onChange={(e) => {
                          setFormData({ ...formData, seatingCapacity: parseInt(e.target.value) || 5 });
                          clearAiFilledField('seatingCapacity');
                          setIsDirty(true);
                        }}
                        sx={aiFilledFields.has('seatingCapacity') ? aiFilledFieldSx : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.color')}
                        value={formData.color}
                        onChange={(e) => {
                          setFormData({ ...formData, color: e.target.value });
                          clearAiFilledField('color');
                          setIsDirty(true);
                        }}
                        sx={aiFilledFields.has('color') ? aiFilledFieldSx : undefined}
                      />
                    </Grid>
                    {isCreating && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                          }}
                        >
                          <Typography variant="subtitle2" gutterBottom>
                            {t('fleet.registration.title', { defaultValue: 'Registration Document (Leja e Qarkullimit)' })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {t('fleet.registration.hint', {
                              defaultValue: 'Upload the vehicle registration document and extract its details with AI to prefill this form.',
                            })}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={<UploadFile />}
                              size="small"
                              disabled={extractRegistrationMutation.isLoading}
                            >
                              {registrationFile ? registrationFile.name : t('fleet.registration.chooseFile', { defaultValue: 'Choose Document' })}
                              <input
                                type="file"
                                hidden
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) handleRegistrationFile(file);
                                }}
                              />
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={extractRegistrationMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                              disabled={!registrationFile || extractRegistrationMutation.isLoading}
                              onClick={handleExtractRegistration}
                            >
                              {extractRegistrationMutation.isLoading
                                ? t('fleet.registration.extracting', { defaultValue: 'Extracting...' })
                                : t('fleet.registration.extractButton', { defaultValue: 'Extract with AI' })}
                            </Button>
                            {registrationFile && !extractRegistrationMutation.isLoading && (
                              <Tooltip title={t('common.removeImage')}>
                                <IconButton
                                  size="small"
                                  onClick={() => setRegistrationFile(null)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={7}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>{t('fields.type')}</InputLabel>
                        <Select
                          value={formData.type}
                          label={t('fields.type')}
                          onChange={(e) => {
                            setFormData({ ...formData, type: e.target.value as VehicleType });
                            setIsDirty(true);
                          }}
                          sx={{ '& .MuiSelect-select': { py: 1 } }}
                        >
                          <MenuItem value={VehicleType.CAR}>{vehicleTypeLabel(t, VehicleType.CAR)}</MenuItem>
                          <MenuItem value={VehicleType.SUV}>{vehicleTypeLabel(t, VehicleType.SUV)}</MenuItem>
                          <MenuItem value={VehicleType.TRUCK}>{vehicleTypeLabel(t, VehicleType.TRUCK)}</MenuItem>
                          <MenuItem value={VehicleType.VAN}>{vehicleTypeLabel(t, VehicleType.VAN)}</MenuItem>
                          <MenuItem value={VehicleType.MOTORCYCLE}>{vehicleTypeLabel(t, VehicleType.MOTORCYCLE)}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>{t('fields.vehicleClass')}</InputLabel>
                        <Select
                          value={formData.vehicleClass}
                          label={t('fields.vehicleClass')}
                          onChange={(e) => {
                            setFormData({ ...formData, vehicleClass: e.target.value as VehicleClass });
                            setIsDirty(true);
                          }}
                          sx={{ '& .MuiSelect-select': { py: 1 } }}
                        >
                          <MenuItem value={VehicleClass.ECONOMY}>{vehicleClassLabel(t, VehicleClass.ECONOMY)}</MenuItem>
                          <MenuItem value={VehicleClass.COMPACT}>{vehicleClassLabel(t, VehicleClass.COMPACT)}</MenuItem>
                          <MenuItem value={VehicleClass.MID_SIZE}>{vehicleClassLabel(t, VehicleClass.MID_SIZE)}</MenuItem>
                          <MenuItem value={VehicleClass.FULL_SIZE}>{vehicleClassLabel(t, VehicleClass.FULL_SIZE)}</MenuItem>
                          <MenuItem value={VehicleClass.LUXURY}>{vehicleClassLabel(t, VehicleClass.LUXURY)}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.dailyRate')}
                        type="number"
                        value={formData.dailyRate}
                        onChange={(e) => {
                          setFormData({ ...formData, dailyRate: parseFloat(e.target.value) || 0 });
                          setIsDirty(true);
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>,
                        }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>{t('fields.status')}</InputLabel>
                        <Select
                          value={formData.status}
                          label={t('fields.status')}
                          onChange={(e) => {
                            setFormData({ ...formData, status: e.target.value as VehicleStatus });
                            setIsDirty(true);
                          }}
                          sx={{ '& .MuiSelect-select': { py: 1 } }}
                        >
                          <MenuItem value={VehicleStatus.AVAILABLE}>{statusLabel(t, VehicleStatus.AVAILABLE)}</MenuItem>
                          <MenuItem value={VehicleStatus.RENTED}>{statusLabel(t, VehicleStatus.RENTED)}</MenuItem>
                          <MenuItem value={VehicleStatus.MAINTENANCE}>{statusLabel(t, VehicleStatus.MAINTENANCE)}</MenuItem>
                          <MenuItem value={VehicleStatus.OUT_OF_SERVICE}>{statusLabel(t, VehicleStatus.OUT_OF_SERVICE)}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.mileage')}
                        type="number"
                        value={formData.mileage}
                        onChange={(e) => {
                          setFormData({ ...formData, mileage: parseInt(e.target.value) || 0 });
                          setIsDirty(true);
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.purchasePrice', 'Purchase Price')}
                        type="number"
                        value={formData.purchasePrice}
                        onChange={(e) => {
                          setFormData({ ...formData, purchasePrice: e.target.value });
                          setIsDirty(true);
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.description')}
                        value={formData.description}
                        onChange={(e) => {
                          setFormData({ ...formData, description: e.target.value });
                          setIsDirty(true);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isFeatured}
                            onChange={(e) => {
                              setFormData({ ...formData, isFeatured: e.target.checked });
                              setIsDirty(true);
                            }}
                          />
                        }
                        label={t('fields.publicSearch')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isAvailableForRental}
                            onChange={(e) => {
                              setFormData({ ...formData, isAvailableForRental: e.target.checked });
                              setIsDirty(true);
                            }}
                          />
                        }
                        label={t('fields.availableForRental')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingOver(true);
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingOver(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingOver(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingOver(false);
                          const file = e.dataTransfer.files?.[0];
                          if (file) handleImageFile(file);
                        }}
                        sx={{
                          border: '2px dashed',
                          borderColor: isDraggingOver ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          p: 2,
                          bgcolor: isDraggingOver ? 'action.hover' : 'transparent',
                          transition: 'background-color 0.2s, border-color 0.2s',
                        }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          {t('common.image')}
                        </Typography>
                        {isDraggingOver && (
                          <Typography variant="body2" color="primary" sx={{ py: 2, textAlign: 'center' }}>
                            {t('common.dropImageHere')}
                          </Typography>
                        )}
                        {(formData.imageUrl || pendingImagePreview) && !isDraggingOver && (
                          <Box sx={{ mb: 2 }}>
                            <CardMedia
                              component="img"
                              image={pendingImagePreview || formData.imageUrl}
                              alt={`${formData.make} ${formData.model}`}
                              sx={{
                                width: '100%',
                                maxWidth: 400,
                                maxHeight: 300,
                                objectFit: 'contain',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          </Box>
                        )}
                        {(isCreating || selectedVehicle) && !isDraggingOver && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={<PhotoCamera />}
                              size="small"
                              disabled={imageUploadMutation.isLoading || (isCreating && createMutation.isLoading)}
                            >
                              {isCreating
                                ? pendingImageFile
                                  ? `${pendingImageFile.name}`
                                  : t('common.chooseImage')
                                : imageUploadMutation.isLoading
                                ? t('common.uploading')
                                : t('common.uploadNewImage')}
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) handleImageFile(file);
                                }}
                              />
                            </Button>
                            {(formData.imageUrl || pendingImageFile) && (
                              <Tooltip title={t('common.removeImage')}>
                                <IconButton
                                  size="small"
                                  onClick={handleClearImage}
                                  disabled={clearImageMutation.isLoading || (isCreating && createMutation.isLoading)}
                                  sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                      bgcolor: 'error.light',
                                      color: 'error.contrastText',
                                    },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('management.deleteVehicle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('dialogs.deleteBody', { item: `"${selectedVehicle?.make} ${selectedVehicle?.model}"` })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isLoading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDocumentsDialog}
        onClose={handleCloseDocumentsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('fleet.documents.title', { defaultValue: 'Vehicle documents' })}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFile />}
                size="small"
                disabled={uploadDocumentMutation.isLoading}
                sx={{ textTransform: 'none' }}
              >
                {t('fleet.documents.choose', { defaultValue: 'Choose document' })}
                <input
                  type="file"
                  hidden
                  accept={documentAccept}
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    event.target.value = '';
                    setSelectedDocumentFile(file);
                  }}
                />
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleUploadDocument}
                disabled={!selectedDocumentFile || uploadDocumentMutation.isLoading || !selectedVehicle}
                sx={{ textTransform: 'none' }}
              >
                {uploadDocumentMutation.isLoading
                  ? t('common.uploading')
                  : t('fleet.documents.upload', { defaultValue: 'Upload' })}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selectedDocumentFile?.name ||
                  t('fleet.documents.noSelection', { defaultValue: 'No document selected' })}
              </Typography>
            </Box>

            {isLoadingVehicleDocuments ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : vehicleDocuments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('fleet.documents.empty', { defaultValue: 'No documents uploaded for this vehicle yet.' })}
              </Typography>
            ) : (
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {vehicleDocuments.map((document) => (
                  <ListItem
                    key={document.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                    }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={t('common.open')}>
                          <IconButton
                            size="small"
                            onClick={() => handlePreviewDocument(document)}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.download')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadDocument(document)}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteDocument(document)}
                            disabled={deleteDocumentMutation.isLoading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {document.originalFileName}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {`${formatFileSize(document.fileSizeBytes)} • ${formatDocumentCreatedAt(document.createdAt)}`}
                        </Typography>
                      }
                      sx={{ pr: 10, my: 0 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDocumentsDialog}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('fleet.bulkImport.title', { defaultValue: 'Import Vehicles from Registration Documents' })}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('fleet.bulkImport.hint', {
              defaultValue:
                'Upload multiple registration documents. Each one is processed with AI and added as a draft vehicle for you to review afterwards.',
            })}
          </Typography>

          {!importRunning && (
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
              size="small"
              sx={{ mb: 2, textTransform: 'none' }}
            >
              {t('fleet.bulkImport.chooseFiles', { defaultValue: 'Choose Documents' })}
              <input
                type="file"
                hidden
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = '';
                  if (files.length > 0) handleImportFilesSelected(files);
                }}
              />
            </Button>
          )}

          {importFiles.length > 0 && !importRunning && (
            <List dense sx={{ mb: 2 }}>
              {importFiles.map((file, index) => (
                <ListItem
                  key={`${file.name}-${index}`}
                  secondaryAction={
                    <IconButton size="small" onClick={() => handleRemoveImportFile(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(0)} KB`} />
                </ListItem>
              ))}
            </List>
          )}

          {importItems.length > 0 && (
            <List dense>
              {importItems.map((item, index) => (
                <ListItem key={`${item.file.name}-${index}`}>
                  <ListItemText
                    primary={item.file.name}
                    secondary={item.status === 'failed' ? item.error : undefined}
                  />
                  <Chip
                    size="small"
                    label={t(`fleet.bulkImport.status.${item.status}`, { defaultValue: item.status })}
                    color={
                      item.status === 'done'
                        ? 'success'
                        : item.status === 'failed'
                        ? 'error'
                        : item.status === 'processing'
                        ? 'info'
                        : 'default'
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {importRunning && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                {t('fleet.bulkImport.processing', { defaultValue: 'Processing...' })}
              </Typography>
            </Box>
          )}

          {!importRunning && importItems.length > 0 && importItems.every((item) => item.status === 'done' || item.status === 'failed') && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('fleet.bulkImport.batchComplete', {
                done: importItems.filter((item) => item.status === 'done').length,
                failed: importItems.filter((item) => item.status === 'failed').length,
                defaultValue: `${importItems.filter((item) => item.status === 'done').length} created, ${importItems.filter((item) => item.status === 'failed').length} failed.`,
              })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importRunning}>
            {t('common.close')}
          </Button>
          {importItems.length > 0 && !importRunning && pendingReviewVehicles.length > 0 && (
            <Button variant="contained" onClick={handleStartReview}>
              {t('fleet.bulkImport.reviewNow', { defaultValue: 'Review Now' })}
            </Button>
          )}
          {importItems.length === 0 && (
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={importFiles.length === 0 || importRunning}
            >
              {t('fleet.bulkImport.startImport', { defaultValue: 'Start Import' })}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onClose={handleKeepEditing}>
        <DialogTitle>{t('dialogs.unsavedTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('dialogs.unsavedBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepEditing}>{t('common.keepEditing')}</Button>
          <Button onClick={handleDiscardChanges} color="error" variant="contained">
            {t('common.discardChanges')}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};
